import { useEffect, useMemo, useRef, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { ChevronsUpDown, Search } from "lucide-react";
import { inventoryApi, inventoryQueryKeys } from "../../shared/api/inventory";
import type { StockRow } from "../../shared/api/inventory";
import { formatDate } from "../../shared/format";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

/**
 * "Chọn lô" — a searchable, paginated combobox over inventory lots (#18604, re-opened).
 *
 * <p>The first attempt at this ticket rendered the whole lot list inline under a filter box. It
 * searched, but it never collapsed: the list held screen space permanently and grew with the
 * clinic's stock. This is the shape QA asked for instead — one closed field until clicked, results
 * fetched a page at a time from `/dispensing/stock/search`, and the next page appended only when
 * the user scrolls to the end of what they already have.
 *
 * <p>Two details worth keeping: the panel is absolutely positioned, so opening it never reflows the
 * page beneath it; and the query is debounced and keyed by keyword, so changing the keyword starts
 * a fresh page 0 rather than appending to results for a word the user has already moved on from.
 */

const PAGE_SIZE = 20;
const DEBOUNCE_MS = 250;

export function LotCombobox({
  value,
  onChange,
}: {
  /** The currently selected lot, or null when nothing is chosen yet. */
  value: StockRow | null;
  onChange: (lot: StockRow | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [needle, setNeedle] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const sentinelRef = useRef<HTMLLIElement>(null);

  // Debounce so a fast typist costs one request, not one per keystroke.
  useEffect(() => {
    const id = window.setTimeout(() => setNeedle(typed.trim()), DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [typed]);

  const lots = useInfiniteQuery({
    queryKey: inventoryQueryKeys.stockSearch(needle),
    queryFn: ({ pageParam }) =>
      inventoryApi.stockSearch({ q: needle, page: pageParam, size: PAGE_SIZE }),
    initialPageParam: 0,
    // Returning undefined is what stops react-query from asking for more once the
    // server says there is nothing left — the "no extra API call at the end" rule.
    getNextPageParam: (last, pages) => (last.hasMore ? pages.length : undefined),
    enabled: open,
  });

  const rows = useMemo(
    () => (lots.data?.pages ?? []).flatMap((p) => p.items),
    [lots.data]
  );

  // Close on an outside click or Esc — either way the page layout is unchanged.
  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // Load the next page when the sentinel at the bottom of the list scrolls into view.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!open || !el || !lots.hasNextPage) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !lots.isFetchingNextPage) {
          lots.fetchNextPage();
        }
      },
      { root: el.closest("ul"), rootMargin: "80px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [open, rows.length, lots.hasNextPage, lots.isFetchingNextPage, lots]);

  function select(lot: StockRow) {
    onChange(lot);
    setOpen(false);
    setTyped("");
  }

  return (
    <div ref={rootRef} className="relative">
      {open ? (
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={inputRef}
            id="recall-batch"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder="Tìm theo tên vaccine hoặc mã lô…"
            className="pl-9"
            aria-label="Tìm lô"
            aria-expanded
            role="combobox"
          />
        </div>
      ) : (
        <button
          type="button"
          id="recall-batch"
          role="combobox"
          aria-expanded={false}
          onClick={() => setOpen(true)}
          className="flex w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-2 text-left text-sm hover:bg-muted"
        >
          {value ? (
            <span className="truncate">
              <span className="font-medium text-foreground">{value.medicineName}</span>
              <span className="text-muted-foreground">
                {" "}
                · {value.lotNumber} · HSD {formatDate(value.expiryDate)}
              </span>
            </span>
          ) : (
            <span className="text-muted-foreground">— Chọn lô —</span>
          )}
          <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" />
        </button>
      )}

      {open && (
        // Absolute + z-20: the list floats over the page instead of pushing it down.
        <ul className="absolute z-20 mt-1 max-h-64 w-full divide-y divide-border overflow-auto rounded-md border border-border bg-background shadow-md">
          {rows.map((lot) => (
            <li key={lot.batchId}>
              <button
                type="button"
                className={cn(
                  "w-full px-3 py-2 text-left text-sm hover:bg-muted",
                  lot.batchId === value?.batchId && "bg-muted"
                )}
                onClick={() => select(lot)}
              >
                <span className="block font-medium text-foreground">{lot.medicineName}</span>
                <span className="block font-mono text-xs text-muted-foreground">
                  {lot.lotNumber} · HSD {formatDate(lot.expiryDate)}
                </span>
              </button>
            </li>
          ))}
          {lots.isPending && (
            <li className="px-3 py-6 text-center text-sm text-muted-foreground">Đang tải…</li>
          )}
          {!lots.isPending && rows.length === 0 && (
            <li className="px-3 py-6 text-center text-sm text-muted-foreground">
              Không tìm thấy lô phù hợp.
            </li>
          )}
          {/* Sentinel: visible only while another page exists, so it doubles as the loading row. */}
          {lots.hasNextPage && (
            <li ref={sentinelRef} className="px-3 py-3 text-center text-sm text-muted-foreground">
              {lots.isFetchingNextPage ? "Đang tải thêm…" : " "}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
